import React, { useCallback, useContext, useMemo, useState } from 'react';
import { DownloadOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import CsvExport from '@components/CsvExport';
import FileUploader from '@components/FileUploader';
import { Button, Space, Tooltip, Modal, Table } from 'antd';
import Text from 'antd/lib/typography/Text';
import { useTranslation } from 'react-i18next';

import ActionTypes from '@utils/deviceActions';
import SessionContext from '@providers/Session/SessionContext';
import { getModelData, getModelDataColumns } from '../modelData';

interface IUUpload {
  actionKey: number;
  setUploadedDevices: any;
  selectedAcronym?: any;
  customInfo?: any;
  variablesData?: string[];
}

const useUpload = ({
  actionKey,
  setUploadedDevices,
  customInfo,
  variablesData,
}: IUUpload) => {
  const { t } = useTranslation(['device-actions-modal']);
  const { isMotBusiness } = useContext(SessionContext);
  const [uploadedFile, setUploadedFile] = useState();

  const fileModelColumns = useMemo(
    () => getModelDataColumns(actionKey, t, isMotBusiness, variablesData),
    [actionKey, isMotBusiness, t, variablesData],
  );

  const fileModelData = useMemo(() => {
    if (actionKey === ActionTypes.ACTIVATION_MULTI_CONNECT) {
      return [
        {
          key: '1',
          iccid: '89553202000000159564',
        },
        {
          key: '2',
          iccid: '89553202000000159847',
        },
        {
          key: '3',
          iccid: '89553202000000159489',
        },
        {
          key: '4',
          iccid: '89553202000000159562',
        },
      ] as any;
    }
    if (actionKey === ActionTypes.ACTIVATION_MOT) {
      return [
        {
          key: '1',
          iccid: '89553202000000159564',
          msisdn: '5503404446239',
          vendor: 'TIM',
          imsi: '724320200047910',
        },
        {
          key: '2',
          iccid: '89553202000000159847',
          msisdn: '5503404446239',
          vendor: 'VIVO',
          imsi: '724320200047911',
        },
        {
          key: '3',
          iccid: '89553202000000159489',
          msisdn: '5503404446239',
          vendor: 'CLARO',
          imsi: '724320200047912',
        },
      ];
    }
    if (actionKey === ActionTypes.SEND_SMS) {
      if (variablesData) {
        return [
          {
            key: '1',
            msisdn: '5503404446239',
            ...variablesData.map(item => ({
              [`${item}`]: 'example',
            })),
          },
          {
            key: '2',
            msisdn: '5503404446239',
            ...variablesData.map(item => ({
              [`${item}`]: 'example',
            })),
          },
          {
            key: '3',
            msisdn: '5503404446239',
            ...variablesData.map(item => ({
              [`${item}`]: 'example',
            })),
          },
          {
            key: '4',
            msisdn: '5503404446239',
            ...variablesData.map(item => ({
              [`${item}`]: 'example',
            })),
          },
        ];
      }
      return [
        {
          key: '1',
          msisdn: '5503404446239',
        },
        {
          key: '2',
          msisdn: '5503404446239',
        },
        {
          key: '3',
          msisdn: '5503404446239',
        },
        {
          key: '4',
          msisdn: '5503404446239',
        },
      ];
    }

    return getModelData(actionKey);
  }, [actionKey, variablesData]);

  const info = useCallback(() => {
    Modal.info({
      title: t('csv-format-example'),
      width: 700,
      content: (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Table
            bordered
            tableLayout="auto"
            size="small"
            columns={fileModelColumns}
            pagination={false}
            dataSource={fileModelData}
            style={{ width: '100%' }}
          />
          {customInfo}
          <CsvExport
            data={fileModelData}
            filename="model-import.csv"
            columns={fileModelColumns}
            separator=","
          >
            <Button
              icon={<DownloadOutlined />}
              size="large"
              type="primary"
              className="button-download"
            >
              {t('download-model')}
            </Button>
          </CsvExport>
        </Space>
      ),

      onOk() {
        document.body.style.removeProperty('overflow');
      },
    });
  }, [customInfo, fileModelColumns, fileModelData, t]);

  const uploadStep = useMemo(
    () => ({
      title: t('steps.upload'),
      content: (
        <>
          <Space>
            <Text>
              {t('select-devices')}
              <Tooltip title={t('help')} placement="right">
                <Button
                  type="link"
                  size="small"
                  icon={<QuestionCircleOutlined />}
                  onClick={info}
                />
              </Tooltip>
            </Text>
          </Space>
          <FileUploader
            style={{ marginTop: 10 }}
            onUpload={setUploadedDevices}
            filesListState={[uploadedFile, setUploadedFile]}
            uploadColumns={fileModelColumns}
            onRemove={() => {
              setUploadedFile(undefined);
              setUploadedDevices([]);
            }}
          />
        </>
      ),
      onPrevStep: () => {
        setUploadedDevices([]);
        setUploadedFile(undefined);
      },
    }),
    [t, info, setUploadedDevices, fileModelColumns, uploadedFile],
  );

  const uploadReinitState = () => {
    setUploadedDevices([]);
    setUploadedFile(undefined);
  };

  return { uploadStep, uploadReinitState };
};

export default useUpload;
